import React from 'react';
import { animated, useSpring } from 'react-spring';
import { Field, Form, Formik } from 'formik';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import Button from '../../../../shared/_TBComponents/Button';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import Text from '../../../../shared/_TBComponents/Text';
import useReset from '../hooks/useReset';

const ValidationErrorContainer = styled('div')`
  background-color: #FFFFFF;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  height: auto;
  padding: 60px 48px;
  position: relative;
  width: 440px;
`;

const FormGroup = styled('div')`
  z-index: 1;

  .error {
    border: 2px solid #CC0404;
  }
`;

const FieldError = styled('div')`
  color: #CC0404;
  font-size: 13px;
  font-weight: 400;
  margin: 4px 0px;
`;

const AnimatedContainer = styled('div')`
  display: flex;
  height: 100%;
  left: 48px;
  position: absolute;
  right: 48px;
  top: 0px;
  width: auto;
  z-index: 0;
`;

const EmailSchema = Yup.object().shape({
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const ValidationError = ({ isIdnty, isSetPassword, validateError }) => {
  const emailAddress = useSelector((state) => state.forgot.ui.emailAddress);
  const isComplete = useSelector((state) => state.forgot.ui.isComplete);
  const isSubmitting = useSelector((state) => state.forgot.ui.isSubmitting);

  const { handleForgot } = useReset();

  const formAnimation = useSpring({
    opacity: isSubmitting || isComplete ? 0 : 1,
    zIndex: 1,
  });
  const spinnerAnimation = useSpring({ opacity: isSubmitting ? 1 : 0, zIndex: 0 });

  const theme = useTheme();

  const isExpired = validateError.status === 401;

  // Expired tokens for set password automatically send a new link to the user
  // and we display that message to the user here
  if (isExpired) {
    return (
      <ValidationErrorContainer>
        <Text styledTextProps={{ fontSize: '34px', fontWeight: '700', textAlign: 'center' }}>A new link was emailed to you</Text>
        <Text styledTextProps={{ fontSize: '19px', fontWeight: '400', textAlign: 'center' }} styledWrapperProps={{ marginTop: '40px' }}>
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={{ __html: validateError.message }} />
        </Text>
      </ValidationErrorContainer>
    );
  }

  // Malformed token error should allow the user to resubmit their email
  // and get a new link to set their password
  if (!isComplete && !isExpired) {
    return (
      <ValidationErrorContainer>
        <Formik
          initialValues={{
            email: '',
          }}
          onSubmit={(values) => {
            handleForgot(values, isIdnty);
          }}
          validationSchema={EmailSchema}
          render={({
            errors,
            touched,
            handleSubmit,
            values,
          }) => (
            <>
              <animated.div style={formAnimation}>
                <Form
                  name={isSetPassword ? 'set-password' : 'reset-password'}
                  id={isSetPassword ? 'set-password' : 'reset-password'}
                >
                  <Text styledTextProps={{ fontSize: '34px', fontWeight: '700' }}>Link not valid</Text>
                  <FormGroup className='form-group'>
                    <Text styledTextProps={{ fontSize: '19px', fontWeight: '400' }} styledWrapperProps={{ margin: '40px 0px 20px' }}>
                      This link was already used or is not valid.
                      Please enter your email to get a new link:
                    </Text>
                    <Field
                      className={`form-control ${errors.email && touched.email ? 'error' : ''}`}
                      id='email-field'
                      type='email'
                      name='email'
                      placeholder='Email'
                    />
                    {errors.email && touched.email && <FieldError>{errors.email}</FieldError>}
                  </FormGroup>
                  <FormGroup className='form-group'>
                    <Button
                      bgColor={isIdnty ? '#00F5F5' : theme.colors.orange}
                      disabled={errors.email || isSubmitting || !values.email}
                      fontSize='14px'
                      fullWidth
                      large
                      lightSpinner
                      name='submit'
                      noBorder
                      onClick={handleSubmit}
                      rounded
                      styledWrapperProps={{
                        marginTop: '20px',
                      }}
                      type='submit'
                      txtColor={isIdnty ? '#000000' : theme.colors.white}
                      upperCase
                    />
                  </FormGroup>
                </Form>
              </animated.div>
              <animated.div style={spinnerAnimation}>
                <AnimatedContainer>
                  <Spinner />
                </AnimatedContainer>
              </animated.div>
            </>
          )}
        />
      </ValidationErrorContainer>
    );
  }

  // User entered their email address and a new link was sent to them
  if (isComplete && !isExpired) {
    return (
      <ValidationErrorContainer>
        <Text styledTextProps={{ fontSize: '34px', fontWeight: '700', textAlign: 'center' }}>A link was emailed to you</Text>
        <Text styledTextProps={{ fontSize: '19px', fontWeight: '400', textAlign: 'center' }} styledWrapperProps={{ marginTop: '40px' }}>
          Check your email, a new link has been sent to
          {' '}
          <b>{emailAddress}</b>
          {' '}
          in order to set your password
        </Text>
      </ValidationErrorContainer>
    );
  }

  // Catch all for errors
  return (
    <ValidationErrorContainer>
      <Text styledTextProps={{ fontSize: '34px', fontWeight: '700', textAlign: 'center' }}>Link not valid</Text>
      <Text styledTextProps={{ fontSize: '19px', fontWeight: '400', textAlign: 'center' }} styledWrapperProps={{ marginTop: '40px' }}>{validateError.message}</Text>
    </ValidationErrorContainer>
  );
};

ValidationError.propTypes = {
  isIdnty: PropTypes.bool,
  isSetPassword: PropTypes.bool,
  validateError: PropTypes.shape({
    status: PropTypes.number,
    message: PropTypes.string,
  }).isRequired,
};

ValidationError.defaultProps = {
  isIdnty: false,
  isSetPassword: false,
};

export default ValidationError;
